import { useState } from 'react';
import { Edit2, FileDown, Share2, Link2, CreditCard, RefreshCw } from 'lucide-react';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Label } from '../../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Quote } from '../../../types';
import { getPublicPdfUrl, handleShareWhatsApp, getPaymentStatusLabel, getPaymentStatusVariant } from './quoteHelpers';
import { generatePaymentLink } from '../../../services/payment.service';
import { useToast } from '../../../contexts/ToastContext';

interface QuoteDetailsDialogProps {
  quote: Quote | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quoteEditMode: boolean;
  quoteStatusUpdating: boolean;
  onEnterEditMode: () => void;
  onUpdateStatus: (quoteId: string, status: 'pending' | 'approved' | 'rejected') => Promise<void>;
  accessToken?: string;
  onQuoteUpdate?: () => Promise<unknown>;
}

export function QuoteDetailsDialog({
  quote,
  open,
  onOpenChange,
  quoteEditMode,
  quoteStatusUpdating,
  onEnterEditMode,
  onUpdateStatus,
  accessToken,
  onQuoteUpdate,
}: QuoteDetailsDialogProps) {
  const toast = useToast();
  const [paymentLinkLoading, setPaymentLinkLoading] = useState(false);
  // Calculate margin rate from items
  const getMarginCalculations = () => {
    if (!quote) return { marginRate: 0, baseAmount: 0 };

    const baseAmount = quote.items.reduce((sum, item) => sum + item.subtotal, 0);
    const customItemsTotal = (quote.customItems || []).reduce((sum, ci) => sum + (ci.value || 0), 0);
    const marginAmount = Math.max(quote.totalAmount - baseAmount - customItemsTotal, 0);
    const marginRate = baseAmount > 0 ? marginAmount / baseAmount : 0;

    return { marginRate, baseAmount };
  };

  const { marginRate, baseAmount } = getMarginCalculations();

  const handleGeneratePaymentLink = async (force = false) => {
    if (!quote || !accessToken) return;

    // If link already exists and not forcing, just copy to clipboard
    if (quote.mpPaymentUrl && !force) {
      try {
        await navigator.clipboard.writeText(quote.mpPaymentUrl);
        toast.success('Link de pagamento copiado!');
      } catch {
        toast.success('Link: ' + quote.mpPaymentUrl);
      }
      return;
    }

    setPaymentLinkLoading(true);
    try {
      const result = await generatePaymentLink(quote.id, accessToken, force);
      try {
        await navigator.clipboard.writeText(result.paymentUrl);
        toast.success(force ? 'Novo link gerado e copiado!' : 'Link de pagamento gerado e copiado!');
      } catch {
        toast.success('Link gerado! Copie manualmente: ' + result.paymentUrl);
      }
      if (onQuoteUpdate) {
        await onQuoteUpdate();
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Erro ao gerar link de pagamento';
      toast.error(errorMessage);
    } finally {
      setPaymentLinkLoading(false);
    }
  };

  // Calculate totals for discount display
  const calculateTotals = () => {
    if (!quote) return null;

    const itemsSubtotalWithMargin = quote.items.reduce((sum, item) => {
      return sum + (item.subtotal * (1 + marginRate));
    }, 0);

    const customItemsTotal = (quote.customItems || []).reduce((sum, item) => {
      return sum + item.value;
    }, 0);

    const subtotalWithMargin = itemsSubtotalWithMargin + customItemsTotal;
    const hasCustomItems = customItemsTotal > 0;
    const hasDiscount = quote.discountType && quote.discountValue;

    if (hasDiscount || hasCustomItems) {
      let discountAmount = 0;
      let discountLabel = '';

      if (hasDiscount) {
        if (quote.discountType === 'percentage') {
          discountAmount = subtotalWithMargin * (quote.discountValue! / 100);
          discountLabel = `Desconto (${quote.discountValue}%)`;
        } else {
          discountAmount = quote.discountValue!;
          discountLabel = 'Desconto';
        }
      }

      const finalTotal = Math.max(0, subtotalWithMargin - discountAmount);

      return { itemsSubtotalWithMargin, customItemsTotal, subtotalWithMargin, discountAmount, discountLabel, finalTotal, hasDiscount, hasCustomItems };
    }

    return { itemsSubtotalWithMargin, customItemsTotal, subtotalWithMargin, hasDiscount: false, hasCustomItems };
  };

  const totals = calculateTotals();

  // Calculate MP preview items showing real prices with a separate discount line
  const getMpPreviewItems = () => {
    if (!quote) return { items: [], total: 0 };

    const items = quote.items.map((item) => {
      const w = (item.width * 100).toFixed(1).replace(/\.0$/, '');
      const h = (item.height * 100).toFixed(1).replace(/\.0$/, '');
      const t = String(item.thickness).replace(/\.0$/, '');
      const unitPrice = item.subtotal * (1 + marginRate) / item.quantity;
      return {
        id: item.id,
        title: `${item.material.name} - ${w}cm x ${h}cm x ${t}mm`,
        quantity: item.quantity,
        unitPrice: Math.round(unitPrice * 100) / 100,
        subtotal: Math.round(unitPrice * item.quantity * 100) / 100,
        isDiscount: false,
      };
    });

    const customItems = (quote.customItems || []).map((ci) => ({
      id: ci.id,
      title: ci.description,
      quantity: 1,
      unitPrice: Math.round(ci.value * 100) / 100,
      subtotal: Math.round(ci.value * 100) / 100,
      isDiscount: false,
    }));

    const allItems: typeof items = [...items, ...customItems];

    // Add discount line if applicable
    if (quote.discountType && quote.discountValue) {
      const rawTotal = allItems.reduce((sum, i) => sum + i.subtotal, 0);
      const discountAmt = quote.discountType === 'percentage'
        ? rawTotal * (quote.discountValue / 100)
        : quote.discountValue;
      const rounded = Math.round(discountAmt * 100) / 100;
      allItems.push({
        id: 'discount',
        title: 'Desconto',
        quantity: 1,
        unitPrice: -rounded,
        subtotal: -rounded,
        isDiscount: true,
      });
    }

    const total = allItems.reduce((sum, i) => sum + i.subtotal, 0);

    return { items: allItems, total: Math.round(total * 100) / 100 };
  };

  const mpPreview = getMpPreviewItems();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        key={`details-${quote?.id}-${quote?.updatedAt || quote?.createdAt}-${quoteEditMode}`}
        className="max-w-[95vw] sm:max-w-3xl lg:max-w-5xl xl:max-w-6xl max-h-[80vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <DialogTitle>Detalhes do Orcamento {quote?.quoteNumber}</DialogTitle>
            {quote?.mpPaymentStatus && (
              <Badge variant={getPaymentStatusVariant(quote.mpPaymentStatus)} className="w-fit">
                Pagamento: {getPaymentStatusLabel(quote.mpPaymentStatus)}
              </Badge>
            )}
          </div>
          <DialogDescription className="break-words">
            Cliente: {quote?.customerName} - {quote?.customerEmail}
          </DialogDescription>
        </DialogHeader>

        {quote && (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
          {/* Left Column - Quote Details */}
          <div className="space-y-4 min-w-0">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-xs text-muted-foreground">Telefone</Label>
                <p className="text-sm">{quote.customerPhone}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Documento</Label>
                <p className="text-sm">{quote.customerDocument}</p>
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">Itens do Orcamento</Label>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-3 mt-3">
                {quote.items.map((item) => {
                  const itemWithMargin = item.subtotal * (1 + marginRate);
                  return (
                    <Card key={item.id} className="shadow-sm overflow-hidden">
                      <CardContent className="p-3 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium break-words">{item.material.name}</div>
                            <div className="text-xs text-muted-foreground">Cor: {item.color || 'Sem cor'}</div>
                          </div>
                          <Badge variant="secondary" className="flex-shrink-0">
                            {item.quantity}x
                          </Badge>
                        </div>

                        <div className="text-sm space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">Dimensoes:</span>
                            <span className="font-medium">
                              {(item.width * 100).toFixed(1)}cm x {(item.height * 100).toFixed(1)}cm x {item.thickness}mm
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">Preco base:</span>
                            <span className="text-muted-foreground">R$ {item.subtotal.toFixed(2)}</span>
                          </div>
                          <div className="flex items-center justify-between border-t pt-1">
                            <span className="text-xs font-medium">Preco c/ margem:</span>
                            <span className="font-semibold text-primary">R$ {itemWithMargin.toFixed(2)}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto mt-3">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Material</TableHead>
                      <TableHead>Cor</TableHead>
                      <TableHead>Dimensoes</TableHead>
                      <TableHead className="text-right">Qtd</TableHead>
                      <TableHead className="text-right">Preco Base</TableHead>
                      <TableHead className="text-right">Preco c/ Margem</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {quote.items.map((item) => {
                      const itemWithMargin = item.subtotal * (1 + marginRate);
                      return (
                        <TableRow key={item.id}>
                          <TableCell className="font-medium">{item.material.name}</TableCell>
                          <TableCell>{item.color || 'Sem cor'}</TableCell>
                          <TableCell className="text-sm">
                            {(item.width * 100).toFixed(1)}cm x {(item.height * 100).toFixed(1)}cm x {item.thickness}mm
                          </TableCell>
                          <TableCell className="text-right">{item.quantity}</TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            R$ {item.subtotal.toFixed(2)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            R$ {itemWithMargin.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Custom Items Section */}
              {quote.customItems && quote.customItems.length > 0 && (
                <div className="mt-4">
                  <Label className="text-sm font-medium">Itens Personalizados</Label>

                  {/* Mobile Card View */}
                  <div className="md:hidden space-y-2 mt-3">
                    {quote.customItems.map((item) => (
                      <Card key={item.id} className="shadow-sm overflow-hidden border-l-4 border-l-blue-500">
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{item.description}</span>
                            <span className="font-semibold text-primary">R$ {item.value.toFixed(2)}</span>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Desktop Table View */}
                  <div className="hidden md:block overflow-x-auto mt-3">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Descricao</TableHead>
                          <TableHead className="text-right">Valor</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {quote.customItems.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="font-medium">{item.description}</TableCell>
                            <TableCell className="text-right font-medium">
                              R$ {item.value.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Totals Section */}
              <div className="mt-4 space-y-2 border-t pt-4">
                {(totals?.hasDiscount || totals?.hasCustomItems) ? (
                  <>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">Subtotal (Itens):</span>
                      <span>R$ {totals.itemsSubtotalWithMargin.toFixed(2)}</span>
                    </div>
                    {totals.hasCustomItems && (
                      <div className="flex justify-between items-center text-sm text-blue-600">
                        <span>Itens Personalizados:</span>
                        <span>+R$ {totals.customItemsTotal.toFixed(2)}</span>
                      </div>
                    )}
                    {totals.hasDiscount && (
                      <div className="flex justify-between items-center text-sm text-destructive">
                        <span>{totals.discountLabel}:</span>
                        <span>-R$ {totals.discountAmount!.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center pt-2 border-t">
                      <span className="font-semibold">Total:</span>
                      <span className="text-lg font-bold">R$ {totals.finalTotal !== undefined ? totals.finalTotal.toFixed(2) : totals.subtotalWithMargin.toFixed(2)}</span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Total:</span>
                    <span className="text-lg font-bold">R$ {quote.totalAmount.toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Alterar Status</Label>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={quote.status === 'pending' ? 'pending' : 'outline'}
                  onClick={() => onUpdateStatus(quote.id, 'pending')}
                  disabled={quoteStatusUpdating}
                  className="flex-1 sm:flex-initial"
                >
                  Pendente
                </Button>
                <Button
                  variant={quote.status === 'approved' ? 'approved' : 'outline'}
                  onClick={() => onUpdateStatus(quote.id, 'approved')}
                  disabled={quoteStatusUpdating}
                  className="flex-1 sm:flex-initial"
                >
                  Aprovar
                </Button>
                <Button
                  variant={quote.status === 'rejected' ? 'rejected' : 'outline'}
                  onClick={() => onUpdateStatus(quote.id, 'rejected')}
                  disabled={quoteStatusUpdating}
                  className="flex-1 sm:flex-initial"
                >
                  Rejeitar
                </Button>
              </div>
            </div>
          </div>

          {/* Right Column - Mercado Pago Preview */}
          <div className="hidden lg:block">
            <Card className="sticky top-0 border-blue-500/30 bg-blue-950/20">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-blue-400">
                  <CreditCard className="h-4 w-4" />
                  Preview Mercado Pago
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Itens como aparecem no checkout do cliente
                </p>

                <div className="space-y-2">
                  {mpPreview.items.map((item) => (
                    <div key={item.id} className={`rounded-md border p-2.5 space-y-1 ${item.isDiscount ? 'border-destructive/30 bg-destructive/5' : 'border-border/50 bg-background/50'}`}>
                      <p className={`text-xs font-medium leading-tight ${item.isDiscount ? 'text-destructive' : ''}`}>{item.title}</p>
                      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                        {item.isDiscount ? (
                          <>
                            <span className="text-destructive">Desconto aplicado</span>
                            <span className="font-medium text-destructive">-R$ {Math.abs(item.subtotal).toFixed(2)}</span>
                          </>
                        ) : (
                          <>
                            <span>{item.quantity}x R$ {item.unitPrice.toFixed(2)}</span>
                            <span className="font-medium text-foreground">R$ {item.subtotal.toFixed(2)}</span>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-border/50 pt-2 flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">Total</span>
                  <span className="text-sm font-bold">R$ {mpPreview.total.toFixed(2)}</span>
                </div>

                {quote.mpPaymentStatus && (
                  <div className="border-t border-border/50 pt-2 space-y-1.5">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-muted-foreground">Status:</span>
                      <Badge variant={getPaymentStatusVariant(quote.mpPaymentStatus)} className="text-[10px] px-1.5 py-0">
                        {getPaymentStatusLabel(quote.mpPaymentStatus)}
                      </Badge>
                    </div>
                    {quote.mpPaymentUpdatedAt && (
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-muted-foreground">Atualizado:</span>
                        <span className="text-foreground">{new Date(quote.mpPaymentUpdatedAt).toLocaleString('pt-BR')}</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="text-[10px] text-muted-foreground/60 pt-1">
                  Ref: {quote.quoteNumber}
                </div>
              </CardContent>
            </Card>
          </div>
          </div>
        )}

        <DialogFooter>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button
              variant="secondary"
              onClick={onEnterEditMode}
              disabled={!quote}
              className="w-full sm:w-auto"
            >
              <Edit2 className="mr-2 h-4 w-4" />
              Editar
            </Button>
            {quote?.pdfToken && (
              <>
                <Button
                  variant="default"
                  onClick={() => quote.pdfToken && window.open(getPublicPdfUrl(quote.pdfToken), '_blank')}
                  disabled={!quote}
                  className="w-full sm:w-auto"
                >
                  <FileDown className="mr-2 h-4 w-4" />
                  Baixar PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleShareWhatsApp(quote)}
                  disabled={!quote}
                  className="w-full sm:w-auto"
                >
                  <Share2 className="mr-2 h-4 w-4" />
                  Compartilhar WhatsApp
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleGeneratePaymentLink(false)}
                  disabled={paymentLinkLoading || !quote || !accessToken}
                  className="w-full sm:w-auto"
                >
                  <Link2 className="mr-2 h-4 w-4" />
                  {paymentLinkLoading
                    ? 'Gerando...'
                    : quote?.mpPaymentUrl
                      ? 'Copiar Link Pagamento'
                      : 'Gerar Link Pagamento'}
                </Button>
                {quote?.mpPaymentUrl && (
                  <Button
                    variant="outline"
                    onClick={() => handleGeneratePaymentLink(true)}
                    disabled={paymentLinkLoading || !accessToken}
                    className="w-full sm:w-auto"
                    title="Gerar novo link com dados atualizados"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Regenerar Link
                  </Button>
                )}
              </>
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">
              Fechar
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
